import { generateCreditPlan, populateStepComprehensive, getCreditPlan } from './assessmentService';

/**
 * Generate a complete credit plan using the two-phase approach
 * @param {Object} assessmentData - The assessment data
 * @param {string} aiAnalysis - The AI analysis from processAssessmentForm
 * @returns {Promise<Object>} - The generated credit plan
 */
export const generateCompleteCreditPlan = async (assessmentData, aiAnalysis, setGenerationProgress) => {
  // Phase 1: Generate the high-level plan structure
  console.log('Phase 1: Generating credit plan structure...');
  setGenerationProgress('Phase 1: Generating credit plan structure...');
  const planResult = await generateCreditPlan({ ...assessmentData, aiAnalysis });
  
  const { planId, planStructure } = planResult;
  
  if (!planStructure) {
    throw new Error('No plan structure received from generateCreditPlan');
  }
  
  console.log('Phase 1 complete. Plan structure generated with', Object.keys(planStructure).length, 'steps');
  setGenerationProgress('Phase 1 complete. Plan structure generated with', Object.keys(planStructure).length, 'steps');
  
  // Phase 2: Populate detailed content for each step
  console.log('Phase 2: Populating step details...');
  setGenerationProgress('Phase 2: Populating step details...');
  const populatedSteps = {};
  
  for (const [stepId, stepData] of Object.entries(planStructure)) {
    if (stepId === 'start') {
      populatedSteps[stepId] = stepData;
      continue;
    }
    
    console.log(`Populating comprehensive expert content for step: ${stepId}`);
    setGenerationProgress(`Populating comprehensive expert content for step: ${stepId}`);
    
    const comprehensiveResult = await populateStepComprehensive({
      planId,
      stepId,
      stepData,
      goal: assessmentData.goal,
      formData: assessmentData.formData,
      subcategory: assessmentData.subcategory
    });
    
    populatedSteps[stepId] = comprehensiveResult.comprehensiveStepData;
    console.log(`Step ${stepId} populated successfully`);
    setGenerationProgress(`Step ${stepId} populated successfully`);
    // Rate limiting delay
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
  
  console.log('Phase 2 complete. All steps populated');
  setGenerationProgress('Phase 2 complete. All steps populated');
  return {
    planId,
    planStructure: populatedSteps,
    goal: assessmentData.goal,
    subcategory: assessmentData.subcategory,
    submittedAt: planResult.submittedAt
  };
};

/**
 * Get a credit plan by ID
 * @param {string} planId - The credit plan ID
 * @returns {Promise<Object>} - The complete credit plan
 */
export const getCompleteCreditPlan = async (planId) => {
  const planResult = await getCreditPlan(planId);
  return planResult;
};

/**
 * Populate comprehensive details for a specific step
 * @param {string} planId - The credit plan ID
 * @param {string} stepId - The step ID
 * @param {Object} stepData - The current step data
 * @param {Object} context - The context data (goal, formData, subcategory)
 * @returns {Promise<Object>} - The updated step data
 */
export const populateSingleStepDetails = async (planId, stepId, stepData, context) => {
  const result = await populateStepComprehensive({
    planId,
    stepId,
    stepData,
    goal: context.goal,
    formData: context.formData,
    subcategory: context.subcategory
  });
  
  return result.comprehensiveStepData;
}; 